import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { LoggerService } from './logger.service';
import { switchMap } from 'rxjs/operators';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  userId: number;
}

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private tokenExpirationSubject = new BehaviorSubject<number | null>(null);
  public tokenExpiration$ = this.tokenExpirationSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object, private logger: LoggerService) {
    // Delay token monitoring to avoid injector issues
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.initializeTokenMonitoring(), 0);
    }
  }

  /**
   * Salva o token de acesso no localStorage
   */
  setAccessToken(token: string): void {
    const isBrowser = isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    if (!isBrowser) return;

    localStorage.setItem('access_token', token);
    
    // Calcula e salva a data de expiração
    const expirationTime = this.getTokenExpirationTime(token);
    if (expirationTime) {
      localStorage.setItem('token_expires_at', expirationTime.toString());
      this.tokenExpirationSubject.next(expirationTime);
    }
  }

  /**
   * Obtém o token de acesso do localStorage
   */
  getAccessToken(): string | null {
    const isBrowser = isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    if (!isBrowser) return null;
    return localStorage.getItem('access_token');
  }

  /**
   * Salva o refresh token no localStorage
   */
  setRefreshToken(token: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    localStorage.setItem('refresh_token', token);
  }

  /**
   * Obtém o refresh token do localStorage
   */
  getRefreshToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return localStorage.getItem('refresh_token');
  }

  /**
   * Salva o ID do usuário no localStorage
   */
  setUserId(userId: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.logger.log('TokenService.setUserId - Salvando userId:', userId);
    localStorage.setItem('user_id', userId.toString());
    // Verificar se foi salvo corretamente
    const saved = localStorage.getItem('user_id');
    this.logger.log('TokenService.setUserId - Valor salvo no localStorage:', saved);
  }

  /**
   * Obtém o ID do usuário do localStorage
   */
  getUserId(): number | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const userId = localStorage.getItem('user_id');
    const parsedId = userId ? parseInt(userId) : null;
    this.logger.log('TokenService.getUserId - Recuperando userId do localStorage:', parsedId);
    return parsedId;
  }

  /**
   * Verifica se o token está expirado
   */
  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  /**
   * Verifica se o token está próximo de expirar (dentro de 5 minutos)
   */
  isTokenExpiringSoon(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = payload.exp - currentTime;
      
      // Considera que está expirando se faltam menos de 5 minutos
      return timeUntilExpiration < 300; // 5 minutos em segundos
    } catch (error) {
      return true;
    }
  }

  /**
   * Obtém o tempo restante até a expiração do token em segundos
   */
  getTimeUntilExpiration(): number {
    const token = this.getAccessToken();
    if (!token) return 0;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return Math.max(0, payload.exp - currentTime);
    } catch (error) {
      return 0;
    }
  }

  /**
   * Obtém a data de expiração do token
   */
  private getTokenExpirationTime(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Converter para milissegundos
    } catch (error) {
      return null;
    }
  }

  /**
   * Inicializa o monitoramento do token
   */
  private initializeTokenMonitoring(): void {
    const isBrowser = isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    if (!isBrowser) return;

    const token = this.getAccessToken();
    if (token) {
      const expirationTime = this.getTokenExpirationTime(token);
      if (expirationTime) {
        this.tokenExpirationSubject.next(expirationTime);
      }
    }
  }

  /**
   * Limpa todos os tokens do localStorage
   */
  clearTokens(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('token_expires_at');
    
    this.tokenExpirationSubject.next(null);
  }

  /**
   * Obtém informações completas do token
   */
  getTokenInfo(): TokenInfo | null {
    const token = this.getAccessToken();
    const userId = this.getUserId();

    if (!token || !userId) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        token: token,
        expiresAt: payload.exp * 1000,
        userId: userId
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Cria um observable que emite quando o token está próximo de expirar
   */
  onTokenExpiringSoon(): Observable<void> {
    return this.tokenExpiration$.pipe(
      switchMap(expirationTime => {
        if (!expirationTime) return [];
        
        const timeUntilExpiration = expirationTime - Date.now();
        const warningTime = 5 * 60 * 1000; // 5 minutos antes
        
        if (timeUntilExpiration <= warningTime) {
          return [void 0]; // Emite imediatamente
        }
        
        const delay = timeUntilExpiration - warningTime;
        return timer(delay).pipe(
          switchMap(() => [void 0])
        );
      })
    );
  }
}

